import { Loader2Icon } from 'lucide-react'
import type { FC } from 'react'
import type { AgentActivity } from '@/features/agent/AgentActivityContext'
import { useElapsedLabel } from '@/hooks/use-elapsed-label'

export interface AgentActivityRowViewProps {
  /** What the agent is doing; null renders nothing. */
  activity: AgentActivity | null
}

/**
 * Pure "the agent is working" row for the thread footer: spinner, label, an
 * optional detail and the time elapsed since `activity.startedAt`. It sits in
 * the sticky footer so it stays visible however far the thread is scrolled. The
 * connected {@link AgentActivityRow} feeds it from the thread and
 * `AgentActivityContext`.
 */
export const AgentActivityRowView: FC<AgentActivityRowViewProps> = ({ activity }) => {
  const elapsed = useElapsedLabel(activity?.startedAt ?? null)
  if (!activity) return null
  return (
    // Phone: label + elapsed share the first line and the detail drops to its
    // own line, instead of two half-truncated texts squeezed side by side.
    <p
      role="status"
      className="flex items-center gap-2 px-1 text-muted-foreground text-xs max-md:flex-wrap max-md:gap-y-0.5"
    >
      <Loader2Icon className="size-3 shrink-0 animate-spin text-primary" />
      <span className="truncate max-md:flex-1">{activity.label}</span>
      {activity.detail && (
        <span className="truncate max-md:order-last max-md:basis-full max-md:pl-5">
          · {activity.detail}
        </span>
      )}
      {elapsed && <span className="ml-auto font-mono tabular-nums">{elapsed}</span>}
    </p>
  )
}
